import React, { useEffect, useState } from 'react';
import { Activity, TrendingUp, Users, AlertCircle, CheckCircle2, XCircle, Clock, Download, RefreshCw } from 'lucide-react';
import { getAuditLogs, getSubscriptionMetrics } from '../services/subscriptionAdminService';

interface DashboardMetrics {
    totalActiveSubscriptions: number;
    totalCancelledSubscriptions: number;
    totalBillingExempt: number;
    recentActions: number;
    trendsData: {
        activeChange: number;
        cancelledChange: number;
    };
}

interface RecentAction {
    id: string;
    timestamp: string;
    adminEmail: string;
    action: string;
    targetEmail: string;
    status: 'success' | 'error' | 'pending';
    details?: string;
}

interface AdminActivityDashboardProps {
    onBack?: () => void;
}

const AdminActivityDashboard: React.FC<AdminActivityDashboardProps> = ({ onBack }) => {
    const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
    const [recentActions, setRecentActions] = useState<RecentAction[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [autoRefresh, setAutoRefresh] = useState(true);

    useEffect(() => {
        loadDashboardData();

        // Auto-refresh every 30 seconds if enabled
        if (autoRefresh) {
            const interval = setInterval(() => {
                loadDashboardData(true); // Silent refresh
            }, 30000);
            return () => clearInterval(interval);
        }
    }, [autoRefresh]);

    const loadDashboardData = async (silent = false) => {
        if (!silent) setLoading(true);
        setError(null);

        try {
            // Load metrics and audit logs in parallel
            const [metricsData, auditLogs] = await Promise.all([
                getSubscriptionMetrics(),
                getAuditLogs({ limit: 50 })
            ]);

            setMetrics(metricsData);

            // Transform audit logs to recent actions
            const actions: RecentAction[] = auditLogs.map((log: any) => ({
                id: log.id,
                timestamp: log.timestamp,
                adminEmail: log.adminEmail,
                action: log.action,
                targetEmail: log.targetAccountEmail || 'Unknown',
                status: log.success ? 'success' : 'error',
                details: log.reason || log.metadata?.details
            }));

            setRecentActions(actions);
        } catch (err) {
            console.error('Error loading dashboard data:', err);
            setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const exportAuditLogs = async () => {
        try {
            const logs = await getAuditLogs({ limit: 1000 });

            // Convert to CSV
            const headers = ['Timestamp', 'Admin', 'Action', 'Target User', 'Status', 'Details'];
            const rows = logs.map((log: any) => [
                new Date(log.timestamp).toLocaleString(),
                log.adminEmail,
                log.action,
                log.targetAccountEmail || 'N/A',
                log.success ? 'Success' : 'Error',
                log.reason || log.metadata?.details || 'N/A'
            ]);

            const csv = [
                headers.join(','),
                ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
            ].join('\n');

            // Download CSV
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `admin-audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error exporting logs:', err);
            alert('Failed to export audit logs');
        }
    };

    const getActionIcon = (action: string) => {
        if (action.includes('cancel')) return <XCircle className="w-5 h-5 text-red-500" />;
        if (action.includes('reactivate') || action.includes('upgrade')) return <CheckCircle2 className="w-5 h-5 text-green-500" />;
        if (action.includes('exempt')) return <AlertCircle className="w-5 h-5 text-yellow-500" />;
        return <Activity className="w-5 h-5 text-blue-500" />;
    };

    const getStatusBadge = (status: 'success' | 'error' | 'pending') => {
        const styles = {
            success: 'bg-green-100 text-green-800 border-green-200',
            error: 'bg-red-100 text-red-800 border-red-200',
            pending: 'bg-yellow-100 text-yellow-800 border-yellow-200'
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[status]}`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    if (loading && !metrics) {
        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="max-w-7xl mx-auto">
                    <div className="animate-pulse space-y-6">
                        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            {[...Array(4)].map((_, i) => (
                                <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
                            ))}
                        </div>
                        <div className="h-96 bg-gray-200 rounded-lg"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error && !metrics) {
        return (
            <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
                <div className="max-w-md bg-white rounded-lg shadow-lg p-8 text-center">
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Dashboard</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button
                        onClick={() => loadDashboardData()}
                        className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Navigation Header */}
            <header className="bg-white shadow-sm border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            {onBack && (
                                <button
                                    onClick={onBack}
                                    className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                                >
                                    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                    <span className="font-medium">Back</span>
                                </button>
                            )}
                            <nav className="flex items-center space-x-2 text-sm">
                                <span className="text-gray-500">Admin Portal</span>
                                <span className="text-gray-400">/</span>
                                <span className="text-gray-900 font-medium">Activity Dashboard</span>
                            </nav>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => setAutoRefresh(!autoRefresh)}
                                className={`px-4 py-2 rounded-lg font-medium transition-colors ${autoRefresh
                                    ? 'bg-green-100 text-green-700 border border-green-300'
                                    : 'bg-gray-100 text-gray-700 border border-gray-300'
                                    }`}
                            >
                                <RefreshCw className={`w-4 h-4 inline mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
                                {autoRefresh ? 'Auto-Refresh ON' : 'Auto-Refresh OFF'}
                            </button>
                            <button
                                onClick={exportAuditLogs}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                            >
                                <Download className="w-4 h-4 inline mr-2" />
                                Export Logs
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                            <Activity className="w-8 h-8 mr-3 text-indigo-600" />
                            Admin Activity Dashboard
                        </h1>
                        <p className="text-gray-600 mt-1">Real-time subscription management insights</p>
                    </div>
                </div>

                {/* Metrics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    {/* Active Subscriptions */}
                    <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 mb-1">Active Subscriptions</p>
                                <p className="text-3xl font-bold text-gray-900">
                                    {metrics?.totalActiveSubscriptions || 0}
                                </p>
                                {metrics?.trendsData?.activeChange !== undefined && metrics.trendsData.activeChange !== 0 && (
                                    <p className={`text-sm mt-2 ${metrics.trendsData.activeChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        <TrendingUp className="w-4 h-4 inline" />
                                        {metrics.trendsData.activeChange > 0 ? '+' : ''}{metrics.trendsData.activeChange}% this week
                                    </p>
                                )}
                            </div>
                            <div className="bg-green-100 rounded-full p-3">
                                <Users className="w-8 h-8 text-green-600" />
                            </div>
                        </div>
                    </div>

                    {/* Cancelled Subscriptions */}
                    <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 mb-1">Cancelled</p>
                                <p className="text-3xl font-bold text-gray-900">
                                    {metrics?.totalCancelledSubscriptions || 0}
                                </p>
                                {metrics?.trendsData?.cancelledChange !== undefined && metrics.trendsData.cancelledChange !== 0 && (
                                    <p className={`text-sm mt-2 ${metrics.trendsData.cancelledChange > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                        {metrics.trendsData.cancelledChange > 0 ? '+' : ''}{metrics.trendsData.cancelledChange}% this week
                                    </p>
                                )}
                            </div>
                            <div className="bg-red-100 rounded-full p-3">
                                <XCircle className="w-8 h-8 text-red-600" />
                            </div>
                        </div>
                    </div>

                    {/* Billing Exempt */}
                    <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-yellow-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 mb-1">Billing Exempt</p>
                                <p className="text-3xl font-bold text-gray-900">
                                    {metrics?.totalBillingExempt || 0}
                                </p>
                                <p className="text-sm text-gray-500 mt-2">Free access granted</p>
                            </div>
                            <div className="bg-yellow-100 rounded-full p-3">
                                <AlertCircle className="w-8 h-8 text-yellow-600" />
                            </div>
                        </div>
                    </div>

                    {/* Recent Actions */}
                    <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-indigo-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 mb-1">Recent Actions</p>
                                <p className="text-3xl font-bold text-gray-900">
                                    {metrics?.recentActions || recentActions.length}
                                </p>
                                <p className="text-sm text-gray-500 mt-2">Last 24 hours</p>
                            </div>
                            <div className="bg-indigo-100 rounded-full p-3">
                                <Clock className="w-8 h-8 text-indigo-600" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Activity Feed */}
                <div className="bg-white rounded-lg shadow-md">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-900">Recent Admin Activity</h2>
                        <p className="text-sm text-gray-600 mt-1">Real-time feed of all admin actions</p>
                    </div>

                    <div className="divide-y divide-gray-200">
                        {recentActions.length === 0 ? (
                            <div className="px-6 py-12 text-center">
                                <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                <p className="text-gray-500">No recent activity</p>
                            </div>
                        ) : (
                            recentActions.map((action) => (
                                <div key={action.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-start space-x-3 flex-1">
                                            <div className="mt-1">
                                                {getActionIcon(action.action)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center space-x-2 mb-1">
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {action.adminEmail}
                                                    </p>
                                                    <span className="text-gray-400">•</span>
                                                    <p className="text-sm text-gray-600">
                                                        {action.action.replace(/_/g, ' ')}
                                                    </p>
                                                </div>
                                                <p className="text-sm text-gray-600">
                                                    Target: <span className="font-medium text-gray-900">{action.targetEmail}</span>
                                                </p>
                                                {action.details && (
                                                    <p className="text-xs text-gray-500 mt-1">{action.details}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-3 ml-4">
                                            {getStatusBadge(action.status)}
                                            <span className="text-xs text-gray-500 whitespace-nowrap">
                                                {new Date(action.timestamp).toLocaleTimeString()}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {recentActions.length > 0 && (
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 text-center">
                            <p className="text-sm text-gray-600">
                                Showing {recentActions.length} most recent actions
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminActivityDashboard;
